import { Check, Circle } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/ui/Avatar'
import { QuoteStatusPill, StatusPill } from '@/components/ui/StatusPill'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import {
  gapText,
  readinessRowLabel,
  type ReadinessRow,
} from '@/features/quotes/quoteReadiness'
import { paymentTermsText } from '@/features/quotes/quoteText'
import type { QuoteSectionKey } from '@/features/quotes/sectionSpy'
import type { QuoteDetail } from '@/types/domain'

/* The right rail on both quote routes: what the reader needs SEEN while they
 * work on the left, rather than what they need to act on.
 *
 * These began as the three-step builder's rail — 340px of context that did not
 * scroll away beside a form the width of a wall. The builder is gone; the cards
 * that survived it are the ones that were never about a step. The CREATE page
 * shows QuoteContextCard, which says what the record is about to be, and the
 * DETAIL page shows QuoteMoneyCard, QuoteOfferFactsCard and, on a draft,
 * QuoteReadinessCard.
 *
 * Nothing here is a control. Every card is READ-ONLY, and every figure on them
 * is the server's (FR-1, FR-3) — a rail that let somebody edit the quote would
 * be a second form competing with the one they are filling in, and a rail that
 * computed a total would be a second opinion about the money.
 */

/** The rail's own section heading. Small caps, muted — it names the card
 *  without competing with the figures under it. */
function AsideHeading({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return <p className={cn('asa-eyebrow mb-3.5', className)}>{children}</p>
}

function AsideCard({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <section
      className={cn(
        'bg-surface border-border-subtle rounded-[var(--radius-lg)] border px-5 py-4 shadow-xs',
        className,
      )}
    >
      {children}
    </section>
  )
}

/** One label-and-value line. The value is right-aligned against the label, so a
 *  column of them reads down the right edge the way the figures do. */
function AsideRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-text-muted shrink-0 text-[12.5px] font-light">
        {label}
      </dt>
      <dd className="text-text-body min-w-0 text-right text-[12.5px]">
        {children}
      </dd>
    </div>
  )
}

/* ── The create page ────────────────────────────────────────────────────────
 *
 * What the record IS, beside the form that is about to create it. The number is
 * the one thing on this card that does not exist yet, and saying so is the
 * point: FR-5 mints it server-side, so a salesperson who expects to choose one
 * finds out here rather than by hunting for the field.
 */
export function QuoteContextCard({ record }: { record?: QuoteDetail }) {
  const { t } = useTranslation()

  return (
    <AsideCard>
      <AsideHeading>{t('quotes.aside.quote')}</AsideHeading>

      <dl className="flex flex-col gap-3">
        <AsideRow label={t('quotes.aside.number')}>
          {record ? (
            <span className="asa-num text-text-heading font-semibold">
              {record.number}
            </span>
          ) : (
            <span className="text-text-muted font-light">
              {t('quotes.aside.numberPending')}
            </span>
          )}
        </AsideRow>

        <AsideRow label={t('quotes.aside.status')}>
          {record ? (
            <QuoteStatusPill status={record.status} />
          ) : (
            /* The status the create will produce, not one the record holds —
               every quote is born a draft (FR-5). Worded from the same
               catalogue the pill uses, so it cannot drift from the state it is
               predicting. */
            <StatusPill tone="neutral">{t('quoteStatus.draft')}</StatusPill>
          )}
        </AsideRow>

        {/* Only once there is a record. Before the create the owner is whoever
            is signed in, and printing a name this card did not read would be
            the rail inventing a fact — the one thing a read-only surface must
            not do. */}
        {record?.ownerName ? (
          <AsideRow label={t('quotes.aside.owner')}>
            <span className="flex items-center justify-end gap-2">
              <Avatar name={record.ownerName} size="sm" />
              <span className="text-text-body">{record.ownerName}</span>
            </span>
          </AsideRow>
        ) : null}
      </dl>
    </AsideCard>
  )
}

/* ── The detail page, on a draft ────────────────────────────────────────────
 *
 * «Готовность к отправке» as the sheet's index (S-3, L-4): what the server
 * needs before the offer may go out, what is merely advisable, and a link from
 * each row to the section where it is fixed.
 *
 * The rows are `quoteReadiness`'s, handed in rather than derived here: the
 * section bar's markers read the same rows, so the card and the bar cannot
 * disagree about which section still needs work. There is no overall "ready"
 * line — whether the offer may go out is `sendBlockedReason` (the server's
 * `sendable` plus the recipient the server also requires), and the page's
 * banner and its Send buttons say it.
 *
 * It is NOT the checklist and does not replace it. The checklist names a fault
 * on a named position; this card names which section it is in. The banner in
 * the left column still carries the detail, because a tick cannot say what is
 * wrong with position 1.
 */
export function QuoteReadinessCard({
  rows,
  onGo,
}: {
  rows: readonly ReadinessRow[]
  /** Scroll to a row's section and focus its heading. */
  onGo: (section: QuoteSectionKey) => void
}) {
  const { t } = useTranslation()

  const groups = [
    {
      key: 'required',
      title: t('quotes.readiness.required'),
      rows: rows.filter((row) => row.required),
    },
    {
      key: 'advised',
      title: t('quotes.readiness.advised'),
      rows: rows.filter((row) => !row.required),
    },
  ] as const

  return (
    <AsideCard>
      <AsideHeading>{t('quotes.aside.readiness')}</AsideHeading>

      <div className="flex flex-col gap-4">
        {groups.map((group) => (
          <div key={group.key} className="flex flex-col gap-1">
            <p className="text-text-muted text-[11.5px] font-light">{group.title}</p>
            <ul className="-mx-2 flex flex-col">
              {group.rows.map((row) => {
                const done = row.gap === undefined
                const label = readinessRowLabel(row.key, t)
                const state = row.gap ? gapText(row.gap, t) : t('quotes.readiness.done')
                return (
                  <li key={row.key}>
                    {/* A LINK to the section, as a button: it moves the page
                        rather than going anywhere, and writes no URL.
                        Named "row: state" — the two stacked spans below would
                        otherwise run together into one word for a screen
                        reader. Starts with the visible label, so the name
                        still matches what a voice user says. */}
                    <button
                      type="button"
                      aria-label={t('quotes.readiness.marker', { row: label, gap: state })}
                      onClick={() => onGo(row.section)}
                      className="hover:bg-surface-hover flex w-full cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors duration-[var(--dur-fast)]"
                    >
                      {/* Two different glyphs, not one glyph in two colours:
                          roughly one man in twelve cannot separate the tick's
                          green from the ring's grey. And the state is said in
                          words beside it — colour never carries it alone. */}
                      {done ? (
                        <Check size={15} className="text-positive mt-px shrink-0" aria-hidden />
                      ) : (
                        <Circle
                          size={15}
                          className={cn(
                            'mt-px shrink-0',
                            row.required ? 'text-attention' : 'text-text-muted',
                          )}
                          aria-hidden
                        />
                      )}
                      <span className="flex min-w-0 flex-col">
                        <span
                          className={cn(
                            'text-[12.5px]',
                            done ? 'text-text-body' : 'text-text-heading',
                          )}
                        >
                          {label}
                        </span>
                        {row.gap ? (
                          <span
                            className={cn(
                              'text-[12px] leading-snug font-light',
                              row.required ? 'text-attention' : 'text-text-muted',
                            )}
                          >
                            {state}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>
    </AsideCard>
  )
}

/* ── The detail page ────────────────────────────────────────────────────────
 *
 * The money, on the one dark surface in the rail.
 *
 * These three figures were the builder's, and they came back to the page that
 * replaced it: the cost, what the client pays and the margin between them are
 * what a quote is FOR, and reading them meant opening the costing and
 * scrolling to a panel. The card carries them beside every section instead. It
 * is the only inverse surface here, so the eye finds it without being told, and
 * it stays found while the sheet beside it scrolls a screen past.
 *
 * It repeats the figures MarginPanel also prints, and knowingly. The panel
 * is the place they are CHANGED — it holds the markup field and the Apply
 * button, and it lives in one section. This card is the place they are SEEN. The
 * two never disagree, because neither of them counts.
 *
 * NOTHING is computed. Every figure is `record.costTotals`, which the server
 * recomputes on every read (FR-1) — two surfaces that each add up the same
 * lines will eventually disagree by a rounding rule, and the one that disagrees
 * about the margin is the one somebody signs.
 */
export function QuoteMoneyCard({ record }: { record: QuoteDetail }) {
  const { t } = useTranslation()

  const totals = record.costTotals

  /* A negative margin is the one figure here that must not be read as good
     news. The inverse surface makes every figure look confident, so the sign is
     carried by more than the minus. */
  const margin = Number(totals?.marginPercent)
  const negative = Number.isFinite(margin) && margin < 0

  const currency = totals?.sell.currency

  /* Said only while there is nothing to say otherwise. Three zeroes on a quote
     nobody has costed yet look like a card that failed to load; the line names
     which section turns them into figures. Once a single line exists the numbers
     speak for themselves, and the sentence would be shelf-space spent on a
     fact the reader can see. */
  const uncosted = (record.costLines ?? []).length === 0

  return (
    <section className="bg-surface-inverse text-text-on-inverse rounded-[var(--radius-lg)] px-5 py-4">
      <AsideHeading className="text-white/70">
        {t('quotes.aside.money')}
      </AsideHeading>

      <dl className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[12.5px] font-light text-white/70">
            {t('quotes.pricing.cost')}
          </dt>
          <dd className="asa-num text-[14px]">
            {formatNumber(totals?.buy.amount, 2)}
            {currency ? (
              <span className="ml-1 text-white/60">{currency}</span>
            ) : null}
          </dd>
        </div>

        <div className="flex items-baseline justify-between gap-3">
          {/* The COSTING's own word for this figure, not a second one of this
              card's: the same string labels the footer of the cost table and
              the markup panel, and one value under two names is two values to
              anybody reading quickly. */}
          <dt className="text-[12.5px] font-light text-white/70">
            {t('quotes.costLines.customerTotal')}
          </dt>
          <dd className="asa-num text-[14px]">
            {formatNumber(totals?.sell.amount, 2)}
            {currency ? (
              <span className="ml-1 text-white/60">{currency}</span>
            ) : null}
          </dd>
        </div>

        <div className="h-px bg-white/15" />

        {/* The markup BESIDE the margin it produces (S-5, O6, L19): «Наценка»
            is what was set, «Маржа» what results. The server's effective
            markup — the stored one while every line is at it, an average
            labelled «в среднем» when they differ. Not blue: the margin below
            is the figure this card leads the eye to. */}
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[12.5px] font-light text-white/70">
            {totals?.markupMixed
              ? t('quotes.pricing.markupAverage')
              : t('quotes.pricing.markupEffective')}
          </dt>
          <dd className="asa-num text-[14px]">
            {totals?.markupPercent === undefined
              ? t('common.empty')
              : `${formatNumber(totals.markupPercent, 2)}%`}
          </dd>
        </div>

        <div className="flex items-baseline justify-between gap-3">
          {/* Named for its denominator, exactly as the markup panel names it:
              the percentage a reader types there is over COST, and this one is
              over revenue. The pair only reads correctly when each says which
              it used. */}
          <dt className="text-[12.5px] font-light text-white/70">
            {t('quotes.pricing.marginOverRevenue')}
          </dt>
          {/* A loss is not printed in the same voice as a profit. Blue is the
              ordinary reading; a negative margin is lifted onto its own light
              chip instead, because the inverse surface flatters every figure on
              it and a red that reads on white is invisible on ink. The minus
              sign carries the same fact for a reader who cannot separate the
              two — colour never says it alone. */}
          <dd
            className={cn(
              'asa-num text-[14px] font-semibold',
              negative
                ? 'bg-negative-soft text-negative rounded-full px-2 py-0.5'
                : 'text-blue-300',
            )}
          >
            {totals?.marginPercent === undefined
              ? t('common.empty')
              : `${formatNumber(totals.marginPercent, 2)}%`}
          </dd>
        </div>
      </dl>

      {uncosted ? (
        <p className="mt-3.5 text-[12px] leading-snug font-light text-white/55">
          {t('quotes.aside.moneyNote')}
        </p>
      ) : null}
    </section>
  )
}

/* ── The detail page ────────────────────────────────────────────────────────
 *
 * The terms as the offer will print them, beside the form that sets them.
 *
 * Four facts, and all four are ones a salesperson is asked about on the phone
 * while looking at this screen. They are the same values the terms card below
 * holds — this is not a second source, it is the subset that fits on a rail —
 * and an absent one renders as an em dash rather than as a blank, because "not
 * agreed yet" is an answer and an empty line is a rendering fault.
 */
export function QuoteOfferFactsCard({ record }: { record: QuoteDetail }) {
  const { t } = useTranslation()
  const format = useFormat()

  const dash = (value?: string) =>
    value && value.trim() !== '' ? value : t('common.empty')

  return (
    <AsideCard>
      <AsideHeading>{t('quotes.aside.offerCarries')}</AsideHeading>

      <dl className="flex flex-col gap-3">
        <AsideRow label={t('quotes.form.incoterm')}>
          {dash(record.incoterm)}
        </AsideRow>
        <AsideRow label={t('quotes.form.paymentTerms')}>
          {/* One of the workbook's five stored values reads in the reader's
              words; wording that predates the list reads as typed; a credit
              term carries its days (S-12). */}
          {dash(paymentTermsText(record, t))}
        </AsideRow>
        <AsideRow label={t('quotes.columns.validUntil')}>
          <span className="asa-num">{dash(format.date(record.validUntil))}</span>
        </AsideRow>
      </dl>
    </AsideCard>
  )
}
